import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { flexRender, type RowData } from '@tanstack/react-table';
import type { LegacyReactTable } from '@tanstack/react-table/legacy';
import { Checkbox } from '@/components/ui/checkbox';
import { TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { actionsHeadClass, headCellClass, selectCellClass, type PinningContext } from './pinning';

export interface DataTableHeadProps<TData extends RowData> {
  table: LegacyReactTable<TData>;
  pinning: PinningContext;
  hasRowActions: boolean;
  /** Ids whose header sits over the last digit of its column, not the first. */
  numericColumns?: readonly string[];
}

const SORT_ICON = { asc: ArrowUp, desc: ArrowDown } as const;

export function DataTableHead<TData extends RowData>({
  table,
  pinning,
  hasRowActions,
  numericColumns,
}: DataTableHeadProps<TData>) {
  const someSelected = table.getIsSomeRowsSelected();
  const allSelected = table.getIsAllRowsSelected();

  return (
    <TableHeader className="sticky top-0 z-30 bg-background">
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className="h-10 hover:bg-transparent">
          {pinning.selectable && (
            <TableHead className={selectCellClass(pinning)}>
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={(checked: boolean | 'indeterminate') =>
                  table.toggleAllRowsSelected(checked === true)
                }
                aria-label={allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
              />
            </TableHead>
          )}

          {headerGroup.headers.map((header, index) => {
            const sorted = header.column.getIsSorted();
            const canSort = header.column.getCanSort();
            const Icon = sorted ? SORT_ICON[sorted] : ChevronsUpDown;
            const numeric = numericColumns?.includes(header.column.id) ?? false;

            return (
              <TableHead
                key={header.id}
                colSpan={header.colSpan}
                aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                // `width` alone is a suggestion in `table-layout: auto`, so the
                // same columns rendered 2015px wide with rows collapsed to
                // «Nom… | Empr… | Ca…» the moment the body was an empty state,
                // and the layout jumped between states. `minWidth` is what the
                // algorithm actually honours, so the grid holds still.
                style={
                  header.column.columnDef.size
                    ? {
                        width: header.column.columnDef.size,
                        minWidth: header.column.columnDef.size,
                      }
                    : undefined
                }
                className={cn(
                  'h-10 px-4 text-xs font-normal text-muted-foreground',
                  numeric && 'text-right',
                  headCellClass(index, pinning),
                )}
              >
                {header.isPlaceholder ? null : canSort ? (
                  <button
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    // `w-full` because a button's auto width is fit-content even
                    // as a flex container: without it the header of a numeric
                    // column cannot align itself to the right edge its figures
                    // hang off, since there is no room in the box to move into.
                    className={cn(
                      'group/sort -mx-1 flex h-7 w-full max-w-full items-center gap-1 rounded-md px-1 transition-colors duration-150 ease-out hover:text-foreground',
                      // `w-full` because a button's auto width is fit-content
                      // even as a flex container: without it a numeric header
                      // has no room in its box to move towards the right edge
                      // its figures hang off. Reversed, the label lands flush
                      // with the last digit and the sort arrow moves inward.
                      numeric && 'flex-row-reverse',
                    )}
                  >
                    {/* Nunca `truncate`: an ellipsised label has a min-content
                        width of zero, so the auto table layout is free to shrink
                        the column below its declared `size` — that is how
                        «Probabilidad» became «Probabili…» and how the whole grid
                        collapsed to «Tí… | V…» over an empty body. Nowrap makes
                        the label the column's real floor: a header always fits
                        its own name, in every state. */}
                    <span className="whitespace-nowrap">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </span>
                    <Icon
                      className={cn(
                        'size-3 shrink-0 transition-opacity duration-150 ease-out',
                        sorted ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60',
                      )}
                      aria-hidden
                    />
                  </button>
                ) : (
                  <span className="block whitespace-nowrap">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </span>
                )}
              </TableHead>
            );
          })}

          {hasRowActions && (
            <TableHead className={actionsHeadClass}>
              <span className="sr-only">Acciones</span>
            </TableHead>
          )}
        </TableRow>
      ))}
    </TableHeader>
  );
}
